import configJSON from 'config.json';
import { CountryCodeList } from 'types/countries';
import {
  AGENTS_ROUTE,
  AREA_LISTINGS_ROUTE,
  EMBER_ROUTE,
  MILITARY_ROUTE,
  ROOT_ROUTE,
} from 'components/dynamic-page/route-matchers/types';
import { SEARCH_PAGE_ROUTE_NAME } from 'types/search_page_types';
import { SiteLanguage } from 'types/locales';
import {
  EXP_REALTY_US_ROOT_DOMAIN,
} from 'constants/domains';
import {
  createFooterConfig,
} from './footer-config';
import {
  createOrganizationConfig,
} from './organization-config';
import {
  homeRouteHandler,
  militaryRouteHandler,
} from './route-handler';
import { createHeaderConfig } from './header-config';
import { agentSearchConfig as agentSearch } from './search-agent-config';
import { createHomeConfig } from './home-config';
import ExpInServiceTheme from './theme-config';
import {
  EXP_IN_SERVICE_TENANT_ID,
  EXP_IN_SERVICE_TENANT_ID_TYPE,
} from '@tenants/types';

import type {
  HeaderConfig,
  HomeConfig,
  Tenant,
  TenantConfigAgentSearch,
  TenantConfigFilters,
  TenantConfigFooter,
  TenantConfigHeader,
  TenantConfigHome,
  TenantConfigSEO,
  TenantConfigOrganization,
  ContentHosts,
  OrganizationConfig,
} from '@tenants/types';
import { Namespaces } from 'types/locales';
import { createLanguageConfig } from '@tenants/utils/configs';

//#region Public Constants

/** The l18n namespaces used by the eXp In Service tenant. */
export const EXP_IN_SERVICE_L18N_NAMESPACES = [
  Namespaces.AGENT_SEARCH,
  Namespaces.EXP_IN_SERVICE,
  Namespaces.COMMON,
  Namespaces.SEARCH,
  Namespaces.FORM_ERROR,
  Namespaces.MODAL,
  Namespaces.INTERNAL_LINKS,
  Namespaces.HOME,
  Namespaces.LISTING,
  Namespaces.HOME_APPRAISAL,
  Namespaces.CONNECTION_REQUEST,
  Namespaces.CONNECT_WITH_AGENT,
] as const;

//#endregion

//#region Config Factories
const header: HeaderConfig = createHeaderConfig(EXP_REALTY_US_ROOT_DOMAIN);
const home: HomeConfig = createHomeConfig();
const footer = createFooterConfig(EXP_REALTY_US_ROOT_DOMAIN);
const organization: OrganizationConfig = createOrganizationConfig();
//#endregion

//#region Public Types
export type ExpInServiceTenantConfig = TenantConfigHeader & TenantConfigHome & TenantConfigFooter & TenantConfigOrganization & TenantConfigAgentSearch & TenantConfigSEO & TenantConfigFilters;
export type ExpInServiceTenant = Tenant<EXP_IN_SERVICE_TENANT_ID_TYPE, typeof ExpInServiceTheme, ExpInServiceTenantConfig>;
//#endregion


/**
 * The eXp In Service tenant.
 *
 * This tenant is used to render the eXp In Service website.
 */
export const expInServiceTenant: ExpInServiceTenant = {
  id: EXP_IN_SERVICE_TENANT_ID,
  hosts: configJSON.hosts[EXP_IN_SERVICE_TENANT_ID],
  contentHosts: configJSON.contentPageApi[EXP_IN_SERVICE_TENANT_ID] as ContentHosts,
  blogApiUrl: configJSON.blogApi[EXP_IN_SERVICE_TENANT_ID],
  theme: ExpInServiceTheme,
  localization: {
    ...createLanguageConfig({
      globalLanguages: [SiteLanguage.English],
      standardLanguages: [SiteLanguage.English],
      globalDefaultLanguage: SiteLanguage.English,
      standardDefaultLanguage: SiteLanguage.English,
      namespaces: EXP_IN_SERVICE_L18N_NAMESPACES,
      allowGlobalLocaleSwitching: false,
    }),
    countries: [CountryCodeList.UNITED_STATES],
    defaultCountry: CountryCodeList.UNITED_STATES,
  },
  config: {
    header,
    home,
    footer,
    organization,
    agentSearch,
    seo: {
      noFollowRoutes: [AREA_LISTINGS_ROUTE, SEARCH_PAGE_ROUTE_NAME], // Apply nofollow to area-listings and search pages
    },
    filters: {
      hideExpiredListings: true, // US feeds don't have expired listings so remove the corresponding filter option
      countryCode: CountryCodeList.UNITED_STATES,
    },
  },
  routes: {
    claimed: [ROOT_ROUTE, MILITARY_ROUTE, SEARCH_PAGE_ROUTE_NAME, AREA_LISTINGS_ROUTE],
    handlers: [
      {
        match: MILITARY_ROUTE,
        handler: militaryRouteHandler,
      },
      {
        match: ROOT_ROUTE,
        handler: homeRouteHandler,
      },
    ],
    excluded: [EMBER_ROUTE, AGENTS_ROUTE],
  },
  debug: {
    forceTenantHeaderValue: `useTenant="${EXP_IN_SERVICE_TENANT_ID}"`,
    forceTenantFeatureValue: EXP_IN_SERVICE_TENANT_ID,
  },
  features: {
    MyLinkMyLead: false,
    AgentSearch: true,
    AccessibilityWidget: false,
    ShowQuebecPopup: false,
  },
  googleTagManager: {
    containerIds: {
      defaultContainerId: configJSON.googleTagManager.containerIdExpInService,
    },
  },
  sso: {
    googleWebClientId: process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID_EXP || '',
    facebookAppId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID_EXP || '',
  },
  sitemap: {
    urls: [],
  },
  robots: {
    disallowDomains: [],
  },
  pvr: {
    phoneNumber: '1-844-539-0722',
  },
  mobileAppBanner: null,
};